/**
 * Push Notification Service for Issue #87
 * Handles FCM registration and push notification events on Android via Capacitor
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';
import webPushService from './webPushService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Queue to store notifications that were tapped before the app was ready to handle them
// This is critical for cold start scenarios
let pendingNotificationAction = null;
let notificationActionHandler = null;

// Set up the notification action listener as early as possible (module load time)
// This catches cold start notification taps before React is ready
if (Capacitor.isNativePlatform()) {
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[PUSH] Early listener - Notification action performed:', action);

    const data = action.notification.data || {};
    const notificationData = {
      referenceType: data.reference_type,
      referenceId: data.reference_id,
      notificationType: data.notification_type
    };

    if (notificationActionHandler) {
      // Handler is ready, call it immediately
      console.log('[PUSH] Handler ready, calling immediately');
      notificationActionHandler(notificationData);
    } else {
      // Handler not ready yet, queue the notification
      console.log('[PUSH] Handler not ready, queuing notification');
      pendingNotificationAction = notificationData;
    }
  });
}

/**
 * Check if push notifications are supported on the current platform
 * @returns {boolean} True if on native mobile platform
 */
export const isPushNotificationsSupported = () => {
  return Capacitor.isNativePlatform() || webPushService.isSupported();
};

/**
 * Request permission for push notifications
 * @returns {Promise<boolean>} True if permission granted
 */
export const requestPermission = async () => {
  if (!isPushNotificationsSupported()) {
    console.log('[PUSH] Not on native platform, skipping permission request');
    return false;
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      const result = await PushNotifications.requestPermissions();
      return result.receive === 'granted';
    }

    return permStatus.receive === 'granted';
  } catch (error) {
    console.error('[PUSH] Error requesting permission:', error);
    return false;
  }
};

/**
 * Register device for push notifications
 * This will trigger the 'registration' event with the FCM token
 */
export const registerForPushNotifications = async () => {
  if (!isPushNotificationsSupported()) {
    console.log('[PUSH] Push notifications not supported on this platform');
    return;
  }

  // Web Push path for browsers/PWA
  if (!Capacitor.isNativePlatform() && webPushService.isSupported()) {
    try {
      const subscription = await webPushService.subscribe();
      if (subscription) {
        console.log('[PUSH] Web Push subscription registered');
      } else {
        console.log('[PUSH] Web Push permission not granted or subscription failed');
      }
    } catch (error) {
      console.warn('[PUSH] Web Push registration failed:', error.message || error);
    }
    return;
  }

  // Native (FCM) path for Android/iOS
  try {
    const permissionGranted = await requestPermission();

    if (!permissionGranted) {
      console.log('[PUSH] Permission not granted');
      return;
    }

    try {
      await PushNotifications.register();
      console.log('[PUSH] Registration initiated');
    } catch (registerError) {
      // FCM registration can fail if google-services.json is missing or invalid
      console.warn('[PUSH] FCM registration failed - push notifications disabled:', registerError.message || registerError);
    }
  } catch (error) {
    // Permission check/request can fail if the plugin isn't properly configured
    console.warn('[PUSH] Error in push notification setup:', error.message || error);
  }
};

/**
 * Send FCM token to backend for storage
 * @param {string} token - FCM registration token
 * @param {string} platform - Device platform ('android' or 'ios')
 */
export const registerTokenWithBackend = async (token, platform = 'android') => {
  try {
    await axios.post(`${API_BASE_URL}/api/devices/register`, {
      fcm_token: token,
      device_platform: platform
    });
    console.log('[PUSH] Token registered with backend');
  } catch (error) {
    console.error('[PUSH] Error registering token with backend:', error);
  }
};

/**
 * Remove FCM token from backend (on logout)
 * @param {string} token - FCM registration token
 */
export const unregisterTokenFromBackend = async (token) => {
  if (!token) return;

  try {
    await axios.delete(`${API_BASE_URL}/api/devices/unregister`, {
      data: { fcm_token: token }
    });
    console.log('[PUSH] Token unregistered from backend');
  } catch (error) {
    console.error('[PUSH] Error unregistering token from backend:', error);
  }
};

/**
 * Initialize push notification listeners
 * Should be called once when the app starts
 * @param {Object} callbacks - Callback functions for various events
 * @param {Function} callbacks.onRegistration - Called when FCM token is received
 * @param {Function} callbacks.onNotificationReceived - Called when notification is received while app is open
 * @param {Function} callbacks.onNotificationTapped - Called when user taps on a notification
 * @param {Function} callbacks.onError - Called when there's a registration error
 */
export const initializePushNotifications = (callbacks = {}) => {
  if (!isPushNotificationsSupported()) {
    console.log('[PUSH] Not on native platform, skipping initialization');
    return () => {}; // Return empty cleanup function
  }

  const {
    onRegistration = () => {},
    onNotificationReceived = () => {},
    onNotificationTapped = () => {},
    onError = () => {}
  } = callbacks;

  // Set up the handler for notification taps (used by the early listener)
  notificationActionHandler = onNotificationTapped;

  // Check if there's a pending notification from cold start
  if (pendingNotificationAction) {
    console.log('[PUSH] Processing pending notification from cold start:', pendingNotificationAction);
    // Use setTimeout to ensure React state updates can complete
    setTimeout(() => {
      onNotificationTapped(pendingNotificationAction);
      pendingNotificationAction = null;
    }, 100);
  }

  // Listen for successful registration
  const registrationListener = PushNotifications.addListener('registration', async (token) => {
    console.log('[PUSH] Registration successful, token:', token.value);

    // Store token locally for later use (e.g., unregistration on logout)
    localStorage.setItem('fcm_token', token.value);

    // Send token to backend
    const platform = Capacitor.getPlatform();
    await registerTokenWithBackend(token.value, platform);

    onRegistration(token.value);
  });

  // Listen for registration errors
  const errorListener = PushNotifications.addListener('registrationError', (error) => {
    console.error('[PUSH] Registration error:', error);
    onError(error);
  });

  // Listen for push notifications received while app is in foreground
  const receivedListener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[PUSH] Notification received:', notification);
    onNotificationReceived(notification);
  });

  // Note: pushNotificationActionPerformed listener is set up at module load time
  // to catch cold start notifications. The handler is set above.

  // Return cleanup function to remove listeners
  return async () => {
    notificationActionHandler = null;
    await registrationListener.remove();
    await errorListener.remove();
    await receivedListener.remove();
  };
};

/**
 * Get the stored FCM token
 * @returns {string|null} The stored FCM token or null
 */
export const getStoredToken = () => {
  return localStorage.getItem('fcm_token');
};

/**
 * Clear the stored FCM token (on logout)
 */
export const clearStoredToken = () => {
  localStorage.removeItem('fcm_token');
};

/**
 * Handle notification tap navigation
 * @param {Object} navigation - Navigation object from NavigationContext
 * @param {Object} notificationData - Notification data containing reference info
 */
export const handleNotificationNavigation = (navigation, notificationData) => {
  const { referenceType, referenceId, notificationType } = notificationData;

  if (!referenceType || !referenceId) {
    // No reference, just go to notifications page
    navigation.onNavigateToNotifications?.();
    return;
  }

  switch (referenceType) {
    case 'post':
      // Issue #87: Open comments section if this is a comment notification
      const openComments = notificationType === 'comment';
      navigation.onNavigateToPost?.(referenceId, openComments);
      break;
    case 'marketplace':
      // Issue #87: Navigate to appropriate tab based on notification type
      if (notificationType === 'favorite_sold') {
        navigation.onNavigateToMeinMarktplatz?.('favorites');
      } else if (notificationType === 'heart') {
        navigation.onNavigateToMeinMarktplatz?.('myObjects');
      } else {
        navigation.onNavigateToMarketplaceDetail?.(referenceId);
      }
      break;
    case 'conversation':
      navigation.onNavigateToChat?.(referenceId);
      break;
    default:
      navigation.onNavigateToNotifications?.();
  }
};

export default {
  isPushNotificationsSupported,
  requestPermission,
  registerForPushNotifications,
  registerTokenWithBackend,
  unregisterTokenFromBackend,
  initializePushNotifications,
  getStoredToken,
  clearStoredToken,
  handleNotificationNavigation,
  webPushService
};
